import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiClock, FiSliders, FiFilter, FiRefreshCw, FiAlertTriangle } = FiIcons;

const CalamityFilters = ({
  timeWindow,
  setTimeWindow,
  minMagnitude,
  setMinMagnitude,
  showOffshore,
  setShowOffshore,
  selectedTypes,
  setSelectedTypes,
  onRefresh,
  isLoading,
  calamityStats,
}) => {
  const timeOptions = [
    { value: '24h', label: '24 Hours' },
    { value: '48h', label: '48 Hours' },
    { value: '72h', label: '3 Days' },
    { value: '7d', label: '7 Days' },
    { value: '30d', label: '30 Days' },
  ];

  const calamityTypes = [
    { id: 'earthquake', label: '🌍 Earthquakes', color: 'bg-red-500' },
    { id: 'volcanic', label: '🌋 Volcanic', color: 'bg-orange-500' },
    { id: 'flood', label: '🌊 Floods', color: 'bg-blue-500' },
    { id: 'tidal', label: '⬆️⬇️ Tides', color: 'bg-cyan-500' },
    { id: 'weather', label: '🌀 Weather', color: 'bg-purple-500' },
    { id: 'tsunami', label: '🌋 Tsunami', color: 'bg-red-600' },
  ];

  const handleTypeToggle = (typeId) => {
    setSelectedTypes((prev) => {
      if (prev.includes(typeId)) {
        return prev.filter((id) => id !== typeId);
      } else {
        return [...prev, typeId];
      }
    });
  };

  const selectAllTypes = () => {
    setSelectedTypes(calamityTypes.map((type) => type.id));
  };

  const clearAllTypes = () => {
    setSelectedTypes([]);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-lg shadow-md p-4 mb-6"
    >
      {/* Alert Stats */}
      {calamityStats && calamityStats.warningCount > 0 && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <SafeIcon icon={FiAlertTriangle} className="text-red-600" />
            <span className="text-sm font-medium text-red-800">
              {calamityStats.warningCount} active warnings requiring attention
            </span>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <SafeIcon icon={FiClock} className="text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Time Window:</span>
            <select
              value={timeWindow}
              onChange={(e) => setTimeWindow(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {timeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center space-x-2">
            <SafeIcon icon={FiSliders} className="text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Min Magnitude:</span>
            <input
              type="range"
              min="4.5"
              max="7.5"
              step="0.1"
              value={minMagnitude}
              onChange={(e) => setMinMagnitude(parseFloat(e.target.value))}
              className="w-20"
            />
            <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
              {minMagnitude.toFixed(1)}
            </span>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <label className="flex items-center space-x-2 text-sm">
            <input
              type="checkbox"
              checked={showOffshore}
              onChange={(e) => setShowOffshore(e.target.checked)}
              className="rounded border-gray-300 focus:ring-blue-500"
            />
            <SafeIcon icon={FiFilter} className="text-gray-500" />
            <span>Include Offshore</span>
          </label>

          <button
            onClick={onRefresh}
            disabled={isLoading}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <SafeIcon icon={FiRefreshCw} className={`${isLoading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Event Type Filters */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-gray-700">Event Types:</span>
          <div className="flex items-center space-x-2">
            <button
              onClick={selectAllTypes}
              className="text-xs text-blue-600 hover:text-blue-800 transition-colors"
            >
              Select All
            </button>
            <span className="text-gray-300">|</span>
            <button
              onClick={clearAllTypes}
              className="text-xs text-gray-600 hover:text-gray-800 transition-colors"
            >
              Clear All
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {calamityTypes.map((type) => (
            <button
              key={type.id}
              onClick={() => handleTypeToggle(type.id)}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                selectedTypes.includes(type.id)
                  ? `${type.color} text-white shadow-md`
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {type.label}
            </button>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export default CalamityFilters;